'use client';

import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

interface Options {
  videoRef: RefObject<HTMLVideoElement | null>;
  initialLoad: boolean;
  initialPlay: boolean;
  armOnIdle?: boolean;
}

export function useVideoArming({ videoRef, initialLoad, initialPlay, armOnIdle = false }: Options) {
  const [shouldLoadVideo, setShouldLoadVideo] = useState(initialLoad);
  const shouldPlayVideoOnLoadRef = useRef(initialPlay);

  const armVideo = useCallback(() => {
    shouldPlayVideoOnLoadRef.current = true;
    setShouldLoadVideo(true);
  }, []);

  useEffect(() => {
    if (!armOnIdle) return;

    if (window.requestIdleCallback) {
      const idleId = window.requestIdleCallback(() => armVideo(), { timeout: 3000 });
      return () => window.cancelIdleCallback?.(idleId);
    }

    const timeoutId = setTimeout(() => armVideo(), 1500);
    return () => clearTimeout(timeoutId);
  }, [armOnIdle, armVideo]);

  useEffect(() => {
    if (!shouldLoadVideo || !shouldPlayVideoOnLoadRef.current) {
      return;
    }

    videoRef.current?.play().catch(() => {});
  }, [shouldLoadVideo, videoRef]);

  return { shouldLoadVideo, armVideo };
}
